import { Case } from 'dashboard/models/case';
import { Device } from 'dashboard/models/device';
import { Document } from 'dashboard/models/document';
import { Verification } from 'dashboard/models/verification';
import { CountryCode } from 'lib/constants/locales';
import { FieldsMap } from 'lib/fields';
import {
  ApiListRelationship,
  ApiModel,
  ApiOptionalRelationship,
  ApiRelationship,
  ApiResource,
} from 'lib/interfaces/api';
import { Iso8601DateTimeString } from 'lib/types';

export interface InquiryMetrics {
  'average-completion-seconds-inquiries': number;
  'total-inquiries': number;
  'fraction-completed-inquiries': number;
}

export enum InquiryStatus {
  Created = 'created',
  Pending = 'pending',
  Completed = 'completed',
  NeedsReview = 'needs_review',
  Approved = 'approved',
  Expired = 'expired',
  Declined = 'declined',
  Failed = 'failed',
}

export enum CheckPassType {
  Required = 'required',
  NotRequired = 'unrequired',
}

export interface InquiryReview {
  comment: string;
  status: InquiryStatus;
}

export enum InquiryBehaviorThreatLevel {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

export type InquiryReusablePersonaRiskSignals = {
  'created-at'?: string;
  'latest-authentication-verify-at'?: string;
  'num-inquiries'?: number;
  'num-unique-ip-addresses'?: number;
};

export enum InquiryReusablePersonaStatus {
  Created = 'created',
  Updated = 'updated',
  Authenticated = 'authenticated',
  Errored = 'errored',
}

export enum InquiryRiskModelLabel {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

export interface InquiryRiskModelResponse {
  version: string;
  label: InquiryRiskModelLabel;
  features: {
    'ip-burst'?: {
      count: number;
      'ip-address': string;
    };
  };
}

export interface InquiryAttributes {
  // Inquiries will always have been created at some point
  'created-at': Iso8601DateTimeString;
  'started-at'?: Iso8601DateTimeString;
  'completed-at'?: Iso8601DateTimeString;
  'expired-at'?: Iso8601DateTimeString;
  'expires-at'?: Iso8601DateTimeString;
  'failed-at'?: Iso8601DateTimeString;
  'decisioned-at'?: Iso8601DateTimeString;
  // Inquiry 2.0 only. Dynamic Flow collects consent timestamps within fields.
  'accepted-policies-at'?: Iso8601DateTimeString;
  'one-time-link-code'?: string;
  'redirect-uri'?: string;
  'reference-id'?: string;
  note?: string;
  status?: InquiryStatus;

  'address-city'?: string;
  'address-country-code'?: CountryCode;
  'address-postal-code'?: string;
  'address-street-1'?: string;
  'address-street-2'?: string;
  'address-subdivision'?: string;
  birthdate?: string;
  'name-full'?: string;
  'name-full-localized'?: string;
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  'email-address'?: string;
  'identification-number'?: string;
  'phone-number'?: string;
  'selfie-photo-url'?: string;
  'social-security-number'?: string;

  decisionable?: boolean;
  'reviewer-comment'?: string;

  'request-spoof-attempts'?: number;
  'user-agent-spoof-attempts'?: number;
  'distraction-events'?: number;
  'shortcut-copies'?: number;
  'shortcut-pastes'?: number;
  'autofill-cancels'?: number;
  'autofill-starts'?: number;
  'completion-time'?: number;
  'hesitation-percentage'?: number;
  'behavior-threat-level'?: InquiryBehaviorThreatLevel;
  'reusable-persona-risk-signals': InquiryReusablePersonaRiskSignals;
  'reusable-persona-status': InquiryReusablePersonaStatus | null;
  'risk-model-response'?: InquiryRiskModelResponse;

  'redacted-at'?: Iso8601DateTimeString;
  'marked-for-cascading-redaction-at'?: Iso8601DateTimeString;
  'parent-marked-for-cascading-redaction-at'?: Iso8601DateTimeString;

  'expiration-after-create-interval-seconds'?: number;
  'expiration-after-resume-interval-seconds'?: number;

  environment?: 'sandbox' | 'production';
  fields: FieldsMap;
}

export interface InquiryRelationships {
  account: ApiRelationship<ApiModel.Account>;
  cases: ApiListRelationship<Case['type']>;
  devices: ApiListRelationship<Device['type']>;
  documents: ApiListRelationship<Document['type']>;
  environment: ApiRelationship<ApiModel.EnvironmentSandbox | ApiModel.EnvironmentProduction>;
  'list-matches': ApiListRelationship<ApiModel.ListMatch>;
  creator: Partial<ApiRelationship<ApiModel.User>>;
  'inquiry-template': ApiOptionalRelationship<ApiModel.InquiryTemplate>;
  'inquiry-template-version': ApiOptionalRelationship<ApiModel.InquiryTemplateVersion>;
  redactor: Partial<ApiRelationship<ApiModel.User>>;
  reports: ApiListRelationship<
    | ApiModel.ReportAdverseMedia
    | ApiModel.ReportEmailAddress
    | ApiModel.ReportFinraBrokerCheck
    | ApiModel.ReportPhoneNumber
    | ApiModel.ReportProfile
    | ApiModel.ReportSecActionLookup
    | ApiModel.ReportSocialMedia
    | ApiModel.ReportSynthetic
    | ApiModel.ReportWatchlist
  >;
  reviewer:
    | ApiRelationship<ApiModel.User>
    | ApiRelationship<ApiModel.WorkflowRun>
    | ApiRelationship<ApiModel.WorkflowRunStepAction>;
  selfies: ApiListRelationship<ApiModel.SelfieCenterOnly | ApiModel.SelfieProfileAndCenter>;
  sessions: ApiListRelationship<ApiModel.InquirySession>;
  tags: ApiListRelationship<ApiModel.Tag>;
  template: ApiOptionalRelationship<ApiModel.Template>;
  theme: ApiOptionalRelationship<ApiModel.Theme>;
  verifications: ApiListRelationship<Verification['type']>;
}

export type Inquiry = ApiResource<InquiryAttributes, InquiryRelationships, ApiModel.Inquiry>;

// N.B. InquiryEvent is from the external inquiry serializer. Event uses external serializers.
export interface InquiryEventAttributes extends InquiryAttributes {
  // Dynamic Flow transition event attributes
  'previous-step-name'?: string;
  'next-step-name'?: string;
  reviewer: string;
  'reviewer-type': ApiModel.WorkflowRun | ApiModel.User | ApiModel.Workflow;
}

export interface InquiryEventRelationships extends Omit<InquiryRelationships, 'reviewer'> {
  reviewer:
    | ApiRelationship<ApiModel.User>
    | ApiRelationship<ApiModel.Workflow>
    | ApiRelationship<ApiModel.WorkflowRun>;
}

export type InquiryEvent = ApiResource<
  InquiryEventAttributes,
  InquiryEventRelationships,
  ApiModel.Inquiry
>;

export interface InquiryRiskDistributionDatum {
  // this will usually refer to completed_at, but needs to be labeled as such for our charting util fns
  created_timestamp: number;
  low: number;
  medium: number;
  high: number;
}

export interface InquiryRiskDistribution {
  data: InquiryRiskDistributionDatum[];
}
